"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facilityPickListSchema = exports.facilityPickSchema = void 0;
/**
 * `RPM-096` — the shape `GET /api/rpm/v1/facilities` returns (`J-213`).
 *
 * THE PICK LIST IS NOT THE FACILITY REGISTRY. `/api/v1/facilities` (`facilitySchema` in
 * `index.ts`) answers "what buildings exist", pages through 545 of them and takes a `state` filter.
 * This answers a different question — "which buildings may this nurse enter a reading against right
 * now" — and its answer is the tenant's profiled buildings narrowed to her own assignments. The two
 * shapes carry the same columns today and are deliberately separate contracts: `index.ts` re-exports
 * this file, so importing `facilitySchema` back into it would be an import cycle, and a pick list
 * that silently inherited a registry field would be a pick list nobody reviewed.
 *
 * NO CURSOR AND NO PAGE. Acceptance rows 1 and 2 are four buildings and one; a nurse picks from what
 * she is assigned to, and a list too long to page is a list she cannot work from either. The
 * type-ahead in the `Combobox` filters what is already returned, which is a filter and not a
 * resolution (`CR-24`).
 *
 * NO FREE-TEXT `name` PARAMETER, AND NO RESOLVE ROUTE. The block's API row states both absences, and
 * they are the requirement: a typed string that the server resolves to a building is the mechanism
 * `CR-24` rejects, because a resolution can be wrong and a pick cannot.
 */
const zod_1 = require("zod");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/**
 * One building, as the chooser shows it.
 *
 * NAME, OFFICIAL NAME, CITY AND STATE TRAVEL TOGETHER, and acceptance row 7 is why: two buildings in
 * one tenant may carry the identical mirror `name`, and the nurse has to tell them apart before she
 * picks. Dropping any of the three would leave her choosing between two identical lines.
 *
 * `officialName` IS NULLABLE BECAUSE THE JOIN IS A LEFT JOIN. `carease.facilities_ext` is a registry
 * overlay and a building can exist operationally without a row in it; an inner join would drop those
 * buildings from the list, and a building missing from a pick list is a building a nurse cannot
 * enter a reading against.
 *
 * `currentResidentCount` IS COMPUTED AT REQUEST TIME AND NEVER READ FROM A STORED COLUMN.
 * `facilities_ext.current_patient_count` disagreed with the computed count for 489 of the mirror's
 * 545 buildings on 2026-09-13 (`J-180`), so the block's API row says the count "is stored nowhere and
 * no stored count is read, so a stale count is a stale read and never a stale row". It counts the
 * residents whose mirror `status` is `Current` and who are not soft-deleted — the same set
 * `RPM-097`'s roster shows with its toggle off, which acceptance row 8 asserts.
 */
exports.facilityPickSchema = zod_1.z
    .object({
    facilityId: MIRROR_ID,
    name: zod_1.z.string(),
    /** From `carease.facilities_ext`. Null where the registry holds no row for this building. */
    officialName: zod_1.z.string().nullable(),
    city: zod_1.z.string().nullable(),
    state: zod_1.z.string().nullable(),
    /** Never null: counting no rows is 0. */
    currentResidentCount: zod_1.z.number().int().nonnegative(),
    /**
     * The building's IANA zone, from its current profile (`CR-11`): the time-taken field names it
     * beside itself, because a typed wall-clock time means nothing without it (`RPM-021`, `J-218`).
     */
    ianaTimeZone: zod_1.z.string(),
    /**
     * How this building is contacted: its routing profile's instruction to the nurse, shown beneath
     * the Facility control word for word and never parsed (`RPM-036`, `CD-4`, `J-225`). Null where
     * the building has no routing profile or its profile records no instruction.
     */
    contactInstruction: zod_1.z.string().nullable(),
})
    .meta({ id: "FacilityPick", description: "One building as the facility chooser lists it" });
/**
 * The whole list.
 *
 * AN EMPTY LIST IS A SUCCESSFUL ANSWER, NOT AN ERROR. The block's Blocking row is explicit: a nurse
 * with no assignment gets `200` and an empty array, and the surface states the condition in words —
 * "a nurse staring at an empty box cannot tell a permission gap from a loading failure". A `403`
 * here would make those two indistinguishable on the wire as well as on the screen.
 */
exports.facilityPickListSchema = zod_1.z.object({ items: zod_1.z.array(exports.facilityPickSchema) }).meta({
    id: "FacilityPickList",
    description: "The buildings this account may enter a reading against right now",
});
//# sourceMappingURL=rpm-facilities.js.map